import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import PriceTicker from "@/components/PriceTicker";
import QuoteModal from "@/components/QuoteModal";
import { COMPANY } from "@/lib/site-data";

const NAV = [
  { label: "About", href: "/#about" },
  { label: "Services", href: "/#services" },
  { label: "Process", href: "/#process" },
  { label: "Products", href: "/#products" },
  { label: "Quality", href: "/#quality" },
  { label: "Industries", href: "/#industries" },
  { label: "Contact", href: "/#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50">
      <PriceTicker />
      <div
        className={`glass-nav border-b border-[#E5E5E5] transition-shadow ${
          scrolled ? "shadow-[0_8px_30px_rgba(0,0,0,0.04)]" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <img
              src={COMPANY.logo}
              alt="Agroya Refinery"
              className="w-11 h-11 lg:w-12 lg:h-12 rounded-full object-contain transition-transform group-hover:scale-105"
              style={{ filter: "drop-shadow(0 4px 12px rgba(212,175,55,0.25))" }}
            />
            <div className="leading-tight">
              <div className="font-heading text-[15px] font-medium text-[#0A0A0A] tracking-tight">Agroya Refinery</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">LLP · Pune</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="text-sm text-[#0A0A0A]/80 hover:text-[#D4AF37] transition-colors font-medium"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Button
              data-testid="header-quote-btn"
              onClick={() => setQuoteOpen(true)}
              className="hidden md:inline-flex btn-gold rounded-md h-10 px-5 text-sm"
            >
              Get a Quote
            </Button>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-trigger" className="lg:hidden p-2 rounded-md border border-[#E5E5E5]">
                  <Menu className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-white">
                <div className="flex flex-col gap-1 pt-8">
                  {NAV.map((n) => (
                    <a
                      key={n.label}
                      href={n.href}
                      onClick={() => setOpen(false)}
                      className="py-3 px-2 text-base border-b border-[#E5E5E5] text-[#0A0A0A] hover:text-[#D4AF37]"
                    >
                      {n.label}
                    </a>
                  ))}
                  <Button
                    onClick={() => { setOpen(false); setQuoteOpen(true); }}
                    className="btn-gold mt-6 h-11"
                    data-testid="mobile-quote-btn"
                  >
                    Get a Quote
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      <QuoteModal open={quoteOpen} onOpenChange={setQuoteOpen} />
    </header>
  );
}
